import {inject, observer} from 'mobx-react';
import * as React from 'react';
import styled from 'styled-components';
import countries, {ICountry} from '../../../../../util/phone-number/countries';
import {action, observable} from 'mobx';
import { SearchIcon } from '../../../../icons';
import {lightenColor} from '../../../../../util/style/lighten-color.util';
import {AppStateModel} from '../../../../../model/app-state.model';
import {CmsButton} from '../../../../cms-button/cms-button';
import {BlockColorType} from '../../../../../enum/block-color.enum';
import {Popover} from '../../../../popover/popover';
import {SMALL_SCREEN_SIZE} from '../../../../../constants';
import {largeMobileMedia} from '../../../../../util/style/large-mobile-media';
import {windowResize$} from '../../../../../util/dom/window-resize.util';
import {Subscription} from 'rxjs';

interface IPhoneNumberCountryPickerProps {
  preferredCountries: string[];
  onCountrySelected: (c: ICountry) => void;
  isOpen: boolean;
  opacity: number;
  width: number;
  trigger: React.ReactNode;
  triggerStyle?: React.CSSProperties;
  onClose: () => void;
  searchPlaceholder: string;
  showAllCountriesLabel: string;
  hideAllCountriesLabel: string;
  closeCountryPickerLabel: string;
  searchIconColor: BlockColorType;
  buttonColor: BlockColorType;
  state?: AppStateModel;
}

interface IPhoneNumberCountryCodeProps {
  onSelect: (c: ICountry) => void;
  country: ICountry;
}
const POPOVER_PADDING_TOP = 80;
const PickerWrapper = styled.div`
  border: 1px solid #D6E3EA;
  background-color: #FFFFFF;
  padding: 5px 0 ${(props: {width: number, height: number, hasPaddingBottom: boolean}) => props.hasPaddingBottom ? '10px' : ''} 0;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: ${(props: {width: number, height: number, hasPaddingBottom: boolean}) => props.width}px;
  max-height: ${(props: {width: number, height: number, hasPaddingBottom: boolean}) => props.height}px;
  overflow-y: auto;
  ${(props: {width: number, height: number, hasPaddingBottom: boolean}) => largeMobileMedia(`
    height: ${props.height}px;
  `)}
`;
const MINIMUM_POPOVER_HEIGHT = 300;
const PreferredCountriesWrapper = styled.div``;
const RestCountriesWrapper = styled.div`
  overflow-y: auto;
  max-height: ${(props: {height: number, hasBottomMargin: boolean}) => props.height}px;
  ${(props: {height: number, hasBottomMargin: boolean}) => props.hasBottomMargin ? 'margin-bottom: 10px' : ''};
`;
const CountryCodeWrapper = styled.div`
  padding: 10px 9px 10px 9px;
  color: #335D77;
  cursor: pointer;
  :hover {
    background-color: #F7FAFC;
  }
  display: flex;
  flex-direction: row;
`;
const SearchWrapper = styled.div`
  margin: 10px 5px 10px 5px;
  padding: 5px;
  color: #335D77;
  border: #D6E3EA solid 2px;
  border-radius: 50px;
  display: flex;
  flex-direction: row;
`;
const SearchInputField = styled.input`
  display: block;
  border: none;
  outline: none 0;
  line-height: 1.4;
  flex: 1 1 auto;
  width: calc(100% - 29px);
  &:focus {
    outline: none 0;
  }
`;

const isMobileSize = () => document.body.clientWidth <= SMALL_SCREEN_SIZE;

@inject('state')
export class PhoneNumberCountryCode extends React.Component<IPhoneNumberCountryCodeProps> {
  onClick = () => {
    this.props.onSelect(this.props.country);
  }
  render() {
    const Icon = this.props.country.icon;
    return <CountryCodeWrapper onClick={this.onClick}>
      <Icon style={{marginRight: 10}} size={15}/>
      <span>{`${this.props.country.name} (${this.props.country.callingCode})`}</span>
    </CountryCodeWrapper>;
  }
}

@inject('state')
@observer
class PhoneNumberCountryPickerInternal extends React.Component<IPhoneNumberCountryPickerProps & {onUpdate: () => void, triggerElement: HTMLDivElement}> {

  @observable showAllCountries: boolean;
  @observable searchFocused: boolean = false;
  @observable searchHovered: boolean = false;
  @observable searchText: string;
  resizeStream: Subscription;

  @action
  onFocus = () => {
    this.searchFocused = true;
  }

  @action
  onBlur = () => {
    this.searchFocused = false;
  }

  @action
  onMouseOver = () => {
    this.searchHovered = true;
  }

  @action
  search = (event) => {
    this.searchText = event.target.value;
    this.props.onUpdate();
  }

  @action
  toggleShowAllCountries = () => {
    this.searchText = '';
    this.showAllCountries = !this.showAllCountries;
    this.props.onUpdate();
  }

  onSelectCountry = (c: ICountry) => {
    this.props.onCountrySelected(c);
  }

  @action
  componentWillMount() {
    this.showAllCountries = this.props.preferredCountries.length === 0;
  }

  componentDidMount() {
    this.resizeStream = windowResize$().subscribe(() => {
      this.forceUpdate(() => {
        this.props.onUpdate();
      });
    });
  }

  componentWillUnmount() {
    this.resizeStream.unsubscribe();
  }

  render() {
    let showAllUi = <></>;
    const isMobileDevice = isMobileSize();
    let popupHeight = isMobileDevice ? document.body.clientHeight : document.body.clientHeight - this.props.triggerElement.getBoundingClientRect().bottom;
    if (popupHeight < MINIMUM_POPOVER_HEIGHT) {
      popupHeight = MINIMUM_POPOVER_HEIGHT;
    }
    const preferredCountriesHeight = (this.props.preferredCountries.length > 0 ? (55 + this.props.preferredCountries.length * 45) : 0);
    if (preferredCountriesHeight > 0 && !this.showAllCountries && !isMobileDevice) {
      popupHeight = preferredCountriesHeight;
    }
    if (preferredCountriesHeight > popupHeight - 100 && !isMobileDevice) {
      popupHeight = preferredCountriesHeight + 100;
    }
    let restOfCountriesHeight = popupHeight - preferredCountriesHeight - 20;
    if (isMobileDevice) {
      restOfCountriesHeight -= 25;
    }
    const countriesRestList = countries
      .filter(x =>
        (!this.searchText ||  x.name.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1 || x.callingCode.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1)
        && (!this.props.preferredCountries || (this.props.preferredCountries && this.props.preferredCountries.every(c => c && c.toLowerCase() !== x.countryCode.toLowerCase()))
        )
      )
      .sort((a: ICountry, b: ICountry) => {
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
      })
      .map(x => <PhoneNumberCountryCode key={x.name} country={x} onSelect={this.onSelectCountry} />);

    const countriesRest = <RestCountriesWrapper height={restOfCountriesHeight - 68} hasBottomMargin={this.props.preferredCountries.length > 0}>
      {countriesRestList}
    </RestCountriesWrapper>;
    if (this.showAllCountries || isMobileDevice) {
      const search = <SearchWrapper>
        <SearchIcon style={{width: 24, height: 24, color: this.searchFocused || this.searchHovered ? this.props.state.colors.getColor(this.props.searchIconColor) : lightenColor(this.props.state.colors.getColor(this.props.searchIconColor), .25), marginRight: 5}} />
        <SearchInputField
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          placeholder={this.props.searchPlaceholder}
          onChange={this.search}
          value={this.searchText}
        />
      </SearchWrapper>;
      showAllUi = <>
        {search}
        {countriesRest}
      </>;
    }

    const preferredCountries = countries
      .filter(x => this.props.preferredCountries && this.props.preferredCountries.some(c => c && c.toLowerCase() === x.countryCode.toLowerCase()))
      .sort((a: ICountry, b: ICountry) => {
        if (!this.props.preferredCountries) {
          return 0;
        }
        const aIndex = this.props.preferredCountries.findIndex(x => x.toLowerCase() === a.countryCode.toLowerCase());
        const bIndex = this.props.preferredCountries.findIndex(x => x.toLowerCase() === b.countryCode.toLowerCase());
        if (aIndex < bIndex) { return -1; }
        if (aIndex > bIndex) { return 1; }
        return 0;
      })
      .map(x => <PhoneNumberCountryCode key={x.name} country={x} onSelect={this.onSelectCountry} />);

    const width = isMobileDevice ? document.body.clientWidth : this.props.width;
    let toRender;
    if (!this.props.isOpen) {
      toRender = null;
    } else {
      toRender = <PickerWrapper width={width} height={popupHeight} hasPaddingBottom={this.props.preferredCountries.length > 0}>
        <PreferredCountriesWrapper>
          {preferredCountries}
        </PreferredCountriesWrapper>
        {showAllUi}
        {this.props.preferredCountries.length > 0 && !isMobileDevice && <CmsButton
            key={Math.random() * 100}
            text={this.showAllCountries ? this.props.hideAllCountriesLabel : this.props.showAllCountriesLabel}
            color={this.props.buttonColor}
            onClick={this.toggleShowAllCountries}
        />}
        {isMobileDevice && <CmsButton
            key={Math.random() * 100}
            text={this.props.closeCountryPickerLabel}
            color={this.props.buttonColor}
            onClick={this.props.onClose}
        />}
      </PickerWrapper>;
    }

    return toRender;
  }
}

@inject('state')
export class PhoneNumberCountryPicker extends React.Component<IPhoneNumberCountryPickerProps> {
  triggerElement: HTMLDivElement;

  onUpdate = () => {
    this.forceUpdate();
  }

  componentDidMount() {
    this.forceUpdate();
  }

  render() {
    return <Popover
        isOpen={this.props.isOpen}
        trigger={<div ref={(element) => this.triggerElement = element}>{this.props.trigger}</div>}
        attachment={isMobileSize() ? 'middle left' : 'top center'}
        closePopover={this.props.onClose}
        containerStyle={{paddingTop: isMobileSize() ? 0 : POPOVER_PADDING_TOP}}
        triggerStyle={this.props.triggerStyle}
        constraints={[
          {
            to: this.triggerElement,
            pin: true,
            attachment: 'top left',
          },
          {
            to: 'scrollParent',
            pin: true,
            attachment: 'top left',
          },
          {
            to: 'window',
            attachment: 'top left',
            pin: true
          }
        ]}
    >
      <PhoneNumberCountryPickerInternal {...this.props} onClose={this.props.onClose} onUpdate={this.onUpdate} triggerElement={this.triggerElement} />
    </Popover>;
  }
}
